import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, CheckCircleIcon, AlertCircleIcon, LoaderIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
// TODO: Replace these with your actual EmailJS credentials
const EMAILJS_SERVICE_ID = 'service_doctorecommerce';
const EMAILJS_TEMPLATE_ID = 'template_moving_form';
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY';
type FormStatus = 'idle' | 'sending' | 'success' | 'error';
export function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<FormStatus>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    setStatus('sending');
    try {
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, EMAILJS_PUBLIC_KEY);
      setStatus('success');
      formRef.current.reset();
    } catch {
      setStatus('error');
    }
  };
  const inputClasses = 'w-full bg-carbon border border-border text-light rounded-md py-3 px-4 text-base focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/30 transition-colors duration-200 placeholder:text-muted/50';
  const labelClasses = 'block text-muted text-sm mb-1.5 font-medium';
  const selectClasses = 'w-full bg-carbon border border-border text-light rounded-md py-3 px-4 text-base focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/30 transition-colors duration-200 appearance-none cursor-pointer';
  return <section id="contacto" className="w-full py-16 md:py-24 px-4 sm:px-6 bg-surface" data-id="element-10">
      <div className="max-w-4xl mx-auto" data-id="element-11">
        <motion.div className="text-center mb-10" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-12">
          <span className="text-gold text-xs sm:text-sm font-bold tracking-widest uppercase mb-3 block" data-id="element-13">
            DIAGNÓSTICO GRATUITO
          </span>
          <h2 className="text-gold text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight" data-id="element-14">
            Cuéntanos sobre tu empresa de Moving
          </h2>
          <p className="text-muted text-base sm:text-lg max-w-2xl mx-auto" data-id="element-15">
            Completa el formulario y te contactamos en menos de 24 horas con un
            plan personalizado para tu negocio.
          </p>
        </motion.div>

        {/* Guarantee Badge */}
        <motion.div className="flex justify-center mb-8" initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} data-id="element-16">
          <div className="inline-flex items-center gap-2 bg-emerald/10 border border-emerald/30 text-emerald px-4 py-2 rounded-full text-sm font-medium" data-id="element-17">
            <ShieldCheckIcon className="w-4 h-4" data-id="element-18" />
            Sin compromiso. Sin contratos. 100% confidencial.
          </div>
        </motion.div>

        {/* Success / Error Messages */}
        {status === 'success' && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center gap-3 bg-emerald/10 border border-emerald/30 text-emerald p-4 rounded-xl mb-6 text-base" data-id="element-19">
            <CheckCircleIcon className="w-5 h-5 shrink-0" data-id="element-20" />
            ¡Mensaje enviado! Te contactaremos en menos de 24 horas.
          </motion.div>}

        {status === 'error' && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl mb-6 text-base" data-id="element-21">
            <AlertCircleIcon className="w-5 h-5 shrink-0" data-id="element-22" />
            Hubo un error. Por favor intenta de nuevo o llámanos al (402)
            872-0639
          </motion.div>}

        {/* Form */}
        <motion.form ref={formRef} onSubmit={handleSubmit} className="bg-carbon border border-border rounded-2xl p-6 sm:p-8 md:p-10 space-y-5" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6,
        delay: 0.15,
        ease: 'easeOut'
      }} data-id="element-23">
          {/* Row 1 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-24">
            <div data-id="element-25">
              <label htmlFor="nombre" className={labelClasses} data-id="element-26">
                Nombre completo *
              </label>
              <input type="text" id="nombre" name="nombre" required placeholder="Tu nombre" className={inputClasses} data-id="element-27" />
            </div>
            <div data-id="element-28">
              <label htmlFor="email" className={labelClasses} data-id="element-29">
                Email *
              </label>
              <input type="email" id="email" name="email" required placeholder="tu@email.com" className={inputClasses} data-id="element-30" />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-31">
            <div data-id="element-32">
              <label htmlFor="telefono" className={labelClasses} data-id="element-33">
                Teléfono *
              </label>
              <input type="tel" id="telefono" name="telefono" required placeholder="(000) 000-0000" className={inputClasses} data-id="element-34" />
            </div>
            <div data-id="element-35">
              <label htmlFor="compania" className={labelClasses} data-id="element-36">
                Nombre de la compañía *
              </label>
              <input type="text" id="compania" name="compania" required placeholder="Tu empresa de Moving" className={inputClasses} data-id="element-37" />
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-38">
            <div data-id="element-39">
              <label htmlFor="instagram" className={labelClasses} data-id="element-40">
                Instagram de la compañía *
              </label>
              <input type="text" id="instagram" name="instagram" required placeholder="@tuempresa" className={inputClasses} data-id="element-41" />
            </div>
            <div data-id="element-42">
              <label htmlFor="website" className={labelClasses} data-id="element-43">
                Website <span className="text-muted/60" data-id="element-44">(opcional)</span>
              </label>
              <input type="url" id="website" name="website" placeholder="https://tuempresa.com" className={inputClasses} data-id="element-45" />
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-46">
            <div data-id="element-47">
              <label htmlFor="clientes_actual" className={labelClasses} data-id="element-48">
                ¿Cuántos clientes tienes por semana? *
              </label>
              <input type="number" id="clientes_actual" name="clientes_actual" required min="0" placeholder="Ej: 5" className={inputClasses} data-id="element-49" />
            </div>
            <div data-id="element-50">
              <label htmlFor="clientes_goal" className={labelClasses} data-id="element-51">
                ¿Cuántos clientes nuevos necesitas por semana? *
              </label>
              <input type="number" id="clientes_goal" name="clientes_goal" required min="0" placeholder="Ej: 15" className={inputClasses} data-id="element-52" />
            </div>
          </div>

          {/* Row 5 */}
          <div data-id="element-53">
            <label htmlFor="publicidad" className={labelClasses} data-id="element-54">
              ¿Has hecho publicidad o ads antes? *
            </label>
            <div className="relative" data-id="element-55">
              <select id="publicidad" name="publicidad" required className={selectClasses} data-id="element-56">
                <option value="" disabled selected data-id="element-57">
                  Selecciona una opción
                </option>
                <option value="No, nunca" data-id="element-58">No, nunca</option>
                <option value="Sí, en Google Ads" data-id="element-59">Sí, en Google Ads</option>
                <option value="Sí, en Meta/Facebook Ads" data-id="element-60">
                  Sí, en Meta/Facebook Ads
                </option>
                <option value="Sí, en múltiples plataformas" data-id="element-61">
                  Sí, en múltiples plataformas
                </option>
                <option value="Sí, con otra agencia" data-id="element-62">
                  Sí, con otra agencia
                </option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-63">
                <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-64">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-65" />
                </svg>
              </div>
            </div>
          </div>

          {/* Row 6 */}
          <div data-id="element-66">
            <label htmlFor="inversion" className={labelClasses} data-id="element-67">
              ¿Cuánto has invertido en publicidad hasta ahora? *
            </label>
            <div className="relative" data-id="element-68">
              <select id="inversion" name="inversion" required className={selectClasses} data-id="element-69">
                <option value="" disabled selected data-id="element-70">
                  Selecciona una opción
                </option>
                <option value="Nada aún" data-id="element-71">Nada aún</option>
                <option value="Menos de $500" data-id="element-72">Menos de $500</option>
                <option value="$500 - $2,000" data-id="element-73">$500 - $2,000</option>
                <option value="$2,000 - $5,000" data-id="element-74">$2,000 - $5,000</option>
                <option value="Más de $5,000" data-id="element-75">Más de $5,000</option>
                <option value="Prefiero no decirlo" data-id="element-76">Prefiero no decirlo</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-77">
                <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-78">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-79" />
                </svg>
              </div>
            </div>
          </div>

          {/* Row 7 */}
          <div data-id="element-80">
            <label htmlFor="mensaje" className={labelClasses} data-id="element-81">
              ¿Algo más que quieras contarnos?{' '}
              <span className="text-muted/60" data-id="element-82">(opcional)</span>
            </label>
            <textarea id="mensaje" name="mensaje" rows={3} placeholder="Cuéntanos más sobre tu empresa, tus retos, o lo que necesitas..." className={`${inputClasses} resize-none`} data-id="element-83" />
          </div>

          {/* Submit */}
          <button type="submit" disabled={status === 'sending'} className="w-full bg-gold text-carbon font-bold text-lg py-4 rounded-md shadow-gold-glow hover:shadow-gold-glow-hover transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-84">
            {status === 'sending' ? <>
                <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-85" />
                Enviando...
              </> : 'ENVIAR MI INFORMACIÓN — QUIERO EL DIAGNÓSTICO'}
          </button>

          <p className="text-muted/60 text-xs text-center" data-id="element-86">
            Al enviar, aceptas que te contactemos para tu diagnóstico gratuito.
          </p>
        </motion.form>
      </div>
    </section>;
}